import React, {
  Fragment,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react'

import {
  object,
  bool,
  array,
  oneOf,
  shape,
  func,
  string,
  number,
} from 'prop-types'

import WithTriangleUiContext from 'Pods/Shared/Hocs/WithTriangleUiContext'
import AdultContentModal from 'Pods/Shared/LazyAdultContentModal'
import ModelCard from 'Pods/Shared/ModelCard/ModelCard'
import getModelsPerRow from 'Pods/Shared/ModelCard/utils/getModelsPerRow'

import getArrayFilters from 'Utils/getArrayFilters'
import isIOS from 'Utils/isIOS'
import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'


import TopCategoryBanner from './TopCategoryBanner'
import useHandlePreviousSearch from './useHandlePreviousSearch'
import { mockedModelSaleOff, mockedCartSaleOff } from '../Static/ModelMocks'
import { mockedSeoTags, mockedBreadcrumbs } from '../Static/OtherMocks'

function List({
  extractingCoreStyles,
  data,
  cart,
  selectedFilters,
  setSelectedFilters,
  trackFilterChange,
  viewerIsAdult = false,
  setViewerIsAdult,
  cardVisibilitySettings,
  cardSizeSetting,
  showCgtVerification,
  browserAttributes = EMPTY_OBJECT,
  shouldTrackSearch,
  setShouldTrackSearch,
  setSearchEnchantmentAction,
  searchEnchantmentAction = EMPTY_OBJECT,
  gaList,
  itemListId,
  filtersOptions,
  currentUserId = null,
  seoTags = EMPTY_ARRAY,
  isMobile,
  isSimilarSearchPossible = false,
  currentOffset,
  isSalesBoostEnabled = false,
  subscriptionBadgesEnabled = false,
  subscriptionFlowEnabled = false,
  premiumListingLabelEnabled = false,
  isUserLoggedIn = false,
  isSSRModelsData = true,
  shouldShowAllItemsOnMobile = false,
  isLoading = false,
}) {
  const [showGallery, setShowGallery] = useState()
  const [galleryToShow, setGalleryToShow] = useState()
  const [isAdultContentModalVisible, toggleModal] = useState(false)
  const [wishListItems, setWishListItems] = useState([])
  const [cartItemIds, setCartItemIds] = useState(cart.cartItemIds)
  const [isIOSDevice, setIsIOSDevice] = useState(false)
  const [isListTruncated, toggleTruncatedList] = useState(isMobile && !shouldShowAllItemsOnMobile)
  const currentBreakpoint = useBreakpoint()
  const modelsPerRow = getModelsPerRow(currentBreakpoint, cardSizeSetting)

  // isMobile is false on the first client render (it matches the server until ListingPage hydrates), so
  // the initial state matches the SSR markup. The cut follows isMobile both ways: trackLastItemInView
  // only expands on mobile, so widening back past the breakpoint has to lift it here.
  useEffect(() => {
    toggleTruncatedList(isMobile && !shouldShowAllItemsOnMobile)
  }, [isMobile, shouldShowAllItemsOnMobile])

  useEffect(() => {
    setCartItemIds(cart.cartItemIds)
  }, [cart])

  const {
    originalIndexes,
    originalSearchQuery,
  } = useHandlePreviousSearch({
    data,
    itemListId,
    searchEnchantmentAction,
    selectedFilters,
    setSearchEnchantmentAction,
    setShouldTrackSearch,
    shouldTrackSearch,
    filtersOptions,
  })

  const originalIndexById = useMemo(
    () => new Map(originalIndexes.map(({ id, index }) => [Number(id), index])),
    [originalIndexes]
  )

  // Derived here (was recomputed every render in mapStateToProps, breaking memo(ModelCard)).
  const currentSelectedFileTypes = useMemo(
    () => getArrayFilters(selectedFilters.fileTypes || []),
    [selectedFilters.fileTypes]
  )

  useEffect(() => {
    window.modelsPerRow = modelsPerRow
  }, [modelsPerRow])

  useEffect(() => {
    setIsIOSDevice(isIOS())
  }, [])

  useEffect(() => {
    const removeCartItemId = () => {
      const currentCartItemIds = (window?.pageConfig?.cart_items || []).map(({ id }) => id)
      setCartItemIds(state => {
        const itemToBeRemoved = state.find(({ itemId }) => !currentCartItemIds.includes(itemId))
        const newState = state.filter(({ itemId }) => itemId !== itemToBeRemoved?.itemId)

        return newState
      })
    }

    window.addEventListener('item-removed-from-cart', removeCartItemId)

    return () => {
      window.removeEventListener('item-removed-from-cart', removeCartItemId)
    }
  }, [])

  const handlePreviewClick = useCallback((props) => {
    setShowGallery(undefined)
    setGalleryToShow({ ...props })
  }, [])

  const handleCloseGallery = useCallback(() => {
    setShowGallery(undefined)
  }, [])

  const trackLastItemInView = useCallback(() => {
    if (isMobile && isListTruncated) {
      toggleTruncatedList(false)
    }
  }, [isMobile, isListTruncated])

  const showAdultModal = useCallback(() => toggleModal(true), [])
  const hideAdultModal = useCallback(() => {
    toggleModal(false)
  }, [])

  const getWishListItems = async () => {
    try {
      const wishlist = await window.fetchJSON('/api/internal/wish-lists/default')

      if (wishlist.data.length > 0) {
        const ids = wishlist.data.map(
          (item) => item?.attributes?.itemId
        ).filter(Boolean) || []
        setWishListItems(ids)
      }
    } catch (error) {
      // to do: add error handling
      // once Sentry is available in listing page
    }
  }

  useEffect(() => {
    if (currentUserId) {
      getWishListItems()
    }
  }, [currentUserId])

  useEffect(() => {
    setShowGallery(galleryToShow)
  }, [galleryToShow])

  useEffect(() => {
    handleCloseGallery()
  }, [selectedFilters])

  // Any client-side results fetch carries query params, and the backend only sends the banner for
  // a URL without them (ListingPageController#isTopBannerVisible), so the response always removes
  // it. Dropping it on the tap that starts the fetch moves the grid inside the tap's input window
  // instead of seconds later, when the jump counts as a layout shift.
  const isTopBannerVisible = !isLoading && (isSSRModelsData ? (
    !isUserLoggedIn
    && browserAttributes
    && browserAttributes?.isTopBannerVisible
    && !browserAttributes?.bottomDescription
    && browserAttributes?.title
  ) :(
    browserAttributes
    && browserAttributes?.isTopBannerVisible
    && !browserAttributes?.bottomDescription
    && browserAttributes?.title
  ))

  if (extractingCoreStyles) {
    return (
      <>
        {/* Render unconditionally during critical-CSS extraction so the banner's */}
        {/* Triangle UI styles (cgt-card, cgt-legacy-heading-h6) are captured into */}
        {/* listing_page_index.critical.css and applied at first paint (avoids FOUC/CLS). */}
        <TopCategoryBanner
          title="title"
          description="description"
          breadcrumbs={mockedBreadcrumbs}
          currentProductType="cg"
          relatedTagsUrlWithoutTag="/3d-models/"
          seoTags={mockedSeoTags}
          breadcrumbsType="Browse by type"
          rootPath="/3d-models/aircraft"
          validBrowserType={[]}
        />
        {Array.from({ length: 4 }).map((_, index) => (
          <ModelCard
            key={index}
            {...mockedModelSaleOff}
            id={String(index)}
            cart={mockedCartSaleOff}
            currentSelectedFileTypes={currentSelectedFileTypes}
            cardVisibilitySettings={{
              title: true,
              cgtStandard: true,
              format: true,
              price: true,
              quickActionsOnHover: true,
              type: true,
            }}
            showCgtVerification
            extractingCoreStyles={extractingCoreStyles}
            trackLastItemInView={trackLastItemInView}
          />
        ))}
      </>
    )
  }

  const truncatedData = useMemo(() => {
    return isListTruncated ? data.slice(0, 3) : data
  }, [isListTruncated, data])

  return (
    <Fragment>
      {isTopBannerVisible && (
        <TopCategoryBanner
          title={browserAttributes?.title}
          isFree={browserAttributes?.searchingFree}
          description={browserAttributes?.description}
          breadcrumbs={browserAttributes?.breadcrumbs}
          currentProductType={browserAttributes?.currentProductType}
          relatedTagsUrlWithoutTag={browserAttributes?.relatedTagsUrlWithoutTag}
          seoTags={seoTags}
          pathName={browserAttributes?.pathName}
          freeToPremiumText={browserAttributes?.freeToPremium}
          breadcrumbsType={browserAttributes?.breadcrumbsType}
          rootPath={browserAttributes?.rootPath}
          validBrowserType={browserAttributes?.validBrowserType}
        />
      )}

      {truncatedData.map((model, index) => {
        return (
          <ModelCard
            viewerIsAdult={viewerIsAdult}
            key={model.id}
            {...model}
            cart={cart}
            modelIndex={index}
            listIndex={currentOffset + index}
            modelsPerRow={modelsPerRow}
            showGallery={showGallery}
            onPreviewClick={handlePreviewClick}
            onCloseGallery={handleCloseGallery}
            lastModelIndex={truncatedData.length - 1}
            totalCount={data.length}
            showAdultModal={showAdultModal}
            currentSelectedFileTypes={currentSelectedFileTypes}
            setSelectedFilters={setSelectedFilters}
            trackFilterChange={trackFilterChange}
            cardVisibilitySettings={cardVisibilitySettings}
            cardSizeSetting={cardSizeSetting}
            setCartItemIds={setCartItemIds}
            cartItemIds={cartItemIds}
            wishListItems={wishListItems}
            setWishListItems={setWishListItems}
            showCgtVerification={showCgtVerification}
            searchingFree={selectedFilters?.pricing?.free === '1'}
            originalIndex={originalIndexById.get(Number(model.id))}
            originalSearchQuery={originalSearchQuery}
            gaList={gaList}
            itemListId={itemListId}
            isIOSDevice={isIOSDevice}
            trackLastItemInView={trackLastItemInView}
            isSimilarSearchPossible={isSimilarSearchPossible}
            isSalesBoostEnabled={isSalesBoostEnabled}
            subscriptionBadgesEnabled={subscriptionBadgesEnabled}
            subscriptionFlowEnabled={subscriptionFlowEnabled}
            premiumListingLabelEnabled={premiumListingLabelEnabled}
          />
        )
      })}
      {isAdultContentModalVisible && (
        <AdultContentModal setViewerIsAdult={setViewerIsAdult} hideAdultModal={hideAdultModal} />
      )}
    </Fragment>
  )
}

List.propTypes = {
  extractingCoreStyles: bool.isRequired,
  data: array.isRequired,
  cart: object.isRequired,
  selectedFilters: object.isRequired,
  setSelectedFilters: func.isRequired,
  viewerIsAdult: bool,
  setViewerIsAdult: func.isRequired,
  cardVisibilitySettings: shape({
    cgtStandard: bool.isRequired,
    format: bool.isRequired,
    price: bool.isRequired,
    quickActionsOnHover: bool.isRequired,
    type: bool.isRequired,
    title: bool.isRequired,
  }).isRequired,
  cardSizeSetting: oneOf(['small', 'medium', 'large']).isRequired,
  showCgtVerification: bool.isRequired,
  browserAttributes: shape({
    bottomDescription: bool,
    description: string,
    title: string,
  }),
  shouldTrackSearch: bool.isRequired,
  setShouldTrackSearch: func.isRequired,
  setSearchEnchantmentAction: func.isRequired,
  searchEnchantmentAction: object,
  gaList: string,
  itemListId: string,
  filtersOptions: object.isRequired,
  currentUserId: number,
  seoTags: array,
  isMobile: bool.isRequired,
  isSimilarSearchPossible: bool,
  currentOffset: number.isRequired,
  isSalesBoostEnabled: bool,
  subscriptionBadgesEnabled: bool,
  subscriptionFlowEnabled: bool,
  premiumListingLabelEnabled: bool,
  isUserLoggedIn: bool,
  isSSRModelsData: bool,
  shouldShowAllItemsOnMobile: bool,
  isLoading: bool,
}

const mapStateToProps = ({
  showAdultModal,
  viewerIsAdult,
  setViewerIsAdult,
  selectedFilters,
  setSelectedFilters,
  trackFilterChange,
  cardVisibilitySettings,
  cardSizeSetting,
  showCgtVerification,
  browserAttributes,
  filtersOptions,
  seoTags,
  isSimilarSearchPossible,
  currentMeta,
  isLoading,
}) => {
  const { currentPage, perPage } = currentMeta
  const currentOffset = (currentPage - 1) * perPage

  return {
    showAdultModal,
    viewerIsAdult,
    setViewerIsAdult,
    selectedFilters,
    setSelectedFilters,
    trackFilterChange,
    cardVisibilitySettings,
    cardSizeSetting,
    showCgtVerification,
    browserAttributes,
    filtersOptions,
    seoTags,
    isSimilarSearchPossible,
    currentOffset,
    isLoading,
  }
}

export default WithTriangleUiContext(mapStateToProps)(List)
